import React, { useState } from 'react';
import { Truck, ChevronDown, ChevronUp, Star, History } from 'lucide-react';
import { BlockShell, ci } from './_shared';
import { priceSourceKey } from 'core/utils/productDto';
import { formatNumberDE, formatQtyDE } from 'core/utils/format';

// Beschaffung: Preis MIT Herkunft, Lieferant (Wunsch/fest), Einkaufsorg/-gruppe,
// Plan-Lieferzeit, Mindestbestellmenge, Bestellmengeneinheit, Rahmenvertrag, Staffeln,
// Bezugsquellen (sources[] aus product_sources + product_price_scales).

// Kompakte Staffel-Liste je Quelle (ausklappbar).
const ScaleList = ({ scales, currency, priceUnit, unit, t, c, isDarkMode }) => {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(scales) || scales.length === 0) return null;
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 text-xs font-medium transition-colors ${
          isDarkMode ? 'text-white/50 hover:text-white/70' : 'text-slate-400 hover:text-slate-600'
        }`}
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {t('productDetail.priceScalesFull')} ({scales.length})
      </button>
      {open && (
        <div className={`mt-1 rounded-lg overflow-hidden ${c.panel}`}>
          {scales.map((sc, i) => (
            <div key={i} className={`flex justify-between px-3 py-1 text-xs ${isDarkMode ? 'border-b border-white/10 last:border-0' : 'border-b border-slate-100 last:border-0'}`}>
              <span className={c.label}>
                {t('productDetail.scaleFrom')
                  .replace('{qty}', formatQtyDE(sc.minQty))
                  .replace('{unit}', unit || '')}
              </span>
              <span className={c.value}>
                {formatNumberDE(sc.amount)} {sc.currency || currency || 'EUR'}
                {(sc.priceUnit && sc.priceUnit > 1) ? ` / ${sc.priceUnit}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Maximale Anzahl sofort sichtbarer Historie-Eintraege.
const ORDER_HISTORY_VISIBLE = 8;

const BlockProcurement = ({ dto, t, isDarkMode }) => {
  const p = dto.procurement || {};
  const c = ci(isDarkMode);
  const price = p.price || {};
  const contract = p.contract || {};
  const scales = price.scales || [];
  const sources = Array.isArray(p.sources) ? p.sources : [];
  const orderHistory = Array.isArray(p.orderHistory) ? p.orderHistory : [];

  const Row = ({ label, value }) =>
    value || value === 0 ? (
      <div className="flex justify-between gap-4 py-1.5">
        <span className={`text-sm ${c.label}`}>{label}</span>
        <span className={`text-sm ${c.value} text-right`}>{value}</span>
      </div>
    ) : null;

  return (
    <BlockShell title={t('productDetail.procurement')} icon={Truck} status={p.status} isDarkMode={isDarkMode} demoLabel={t('productDetail.demoFromSap')}>
      <div className={`divide-y ${isDarkMode ? 'divide-white/10' : 'divide-slate-100'}`}>
        <Row
          label={t('productDetail.priceNet')}
          value={
            <span className="inline-flex items-center gap-2">
              <span className="text-brand font-semibold">{formatNumberDE(price.netPrice || 0)} {price.currency || 'EUR'}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand/5 text-brand border border-brand/15">{t(priceSourceKey(price.source))}</span>
            </span>
          }
        />
        <Row label={t('productDetail.orderUnit')} value={price.orderUnit} />
        <Row label={t('productDetail.preferredVendor')} value={p.preferredVendor && p.preferredVendor.name} />
        <Row label={t('productDetail.fixedVendor')} value={p.fixedVendor && p.fixedVendor.name} />
        <Row label={t('productDetail.purchasingOrg')} value={p.purchasingOrg} />
        <Row label={t('productDetail.purchasingGroup')} value={p.purchasingGroup} />
        <Row label={t('productDetail.leadTime')} value={p.plannedDeliveryTime} />
        <Row label={t('productDetail.minOrderQuantity')} value={p.minOrderQty ? `${formatQtyDE(p.minOrderQty)} ${price.orderUnit || ''}`.trim() : null} />
        <Row label={t('productDetail.infoRecord')} value={p.infoRecord && p.infoRecord.exists ? p.infoRecord.number : null} />
        {contract.exists ? (
          <Row
            label={t('productDetail.framework')}
            value={`${contract.number || ''}${contract.validTo ? ` · ${t('productDetail.validTo')} ${contract.validTo}` : ''}`}
          />
        ) : (
          <div className="flex justify-between gap-4 py-1.5">
            <span className={`text-sm ${c.label}`}>{t('productDetail.framework')}</span>
            <span className={`text-sm italic ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>{t('productDetail.noContract')}</span>
          </div>
        )}
      </div>

      {scales.length > 0 && (
        <div className="mt-4">
          <div className={`text-xs ${c.label} mb-1`}>{t('productDetail.priceScales')}</div>
          <div className={`${c.panel} divide-y ${isDarkMode ? 'divide-white/10' : 'divide-slate-100'}`}>
            {scales.map((s, i) => (
              <div key={i} className="flex justify-between px-3 py-1.5 text-sm">
                <span className={c.label}>{t('productDetail.fromQty')} {s.fromQty}</span>
                <span className={c.value}>{formatNumberDE(s.price || 0)} {price.currency || 'EUR'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bezugsquellen-Abschnitt — nur wenn sources[] vorhanden */}
      {sources.length > 0 && (
        <div className="mt-5">
          <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
            {t('productDetail.sources')}
          </p>
          <div className="space-y-3">
            {sources.map((src, idx) => (
              <div
                key={idx}
                className={`rounded-xl p-3 ${
                  src.isPrimary
                    ? isDarkMode
                      ? 'bg-blue-600/10 border border-blue-400/25'
                      : 'bg-brand/5 border border-brand/20'
                    : isDarkMode
                    ? 'bg-white/5 border border-white/10'
                    : 'bg-slate-50 border border-slate-200'
                }`}
              >
                {/* Lieferant + Primär-Badge */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <span className={`text-sm font-medium ${c.value}`}>
                      {src.vendorName || src.vendor || '—'}
                    </span>
                    {src.vendor && (
                      <span className={`ml-2 text-xs ${c.label}`}>({src.vendor})</span>
                    )}
                  </div>
                  {src.isPrimary && (
                    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-brand/10 text-brand'
                    }`}>
                      <Star className="w-2.5 h-2.5" />
                      {t('productDetail.primarySource')}
                    </span>
                  )}
                </div>

                {/* Detail-Zeilen */}
                <div className={`grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs`}>
                  {src.vendorMaterialNo && (
                    <>
                      <span className={c.label}>{t('productDetail.vendorMaterialNo')}</span>
                      <span className={`${c.value} text-right`}>{src.vendorMaterialNo}</span>
                    </>
                  )}
                  {src.netPrice != null && (
                    <>
                      <span className={c.label}>{t('productDetail.priceNet')}</span>
                      <span className={`${c.value} text-right font-medium`}>
                        {formatNumberDE(src.netPrice)} {src.currency || 'EUR'}
                        {src.priceUnit && src.priceUnit > 1 ? ` / ${src.priceUnit} ${src.orderUnit || ''}` : src.orderUnit ? ` / ${src.orderUnit}` : ''}
                      </span>
                    </>
                  )}
                  {src.plannedDeliveryTime != null && (
                    <>
                      <span className={c.label}>{t('productDetail.deliveryTime')}</span>
                      <span className={`${c.value} text-right`}>{src.plannedDeliveryTime}</span>
                    </>
                  )}
                  {src.minOrderQty != null && src.minOrderQty > 0 && (
                    <>
                      <span className={c.label}>{t('productDetail.minOrderQty')}</span>
                      <span className={`${c.value} text-right`}>{formatQtyDE(src.minOrderQty)} {src.orderUnit || ''}</span>
                    </>
                  )}
                  {src.incoterms && (
                    <>
                      <span className={c.label}>{t('productDetail.incoterms')}</span>
                      <span className={`${c.value} text-right`}>{src.incoterms}</span>
                    </>
                  )}
                </div>

                {/* Staffeln je Quelle */}
                <ScaleList
                  scales={src.scales}
                  currency={src.currency}
                  priceUnit={src.priceUnit}
                  unit={src.orderUnit || (dto.identity && dto.identity.baseUnit) || ''}
                  t={t}
                  c={c}
                  isDarkMode={isDarkMode}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Bestellhistorie — nur wenn Eintraege vorhanden */}
      {orderHistory.length > 0 && (
        <div className="mt-5">
          <p className={`text-xs font-semibold uppercase tracking-wide mb-3 flex items-center gap-1.5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
            <History className="w-3.5 h-3.5" />
            {t('productDetail.orderHistory')}
          </p>
          {/* Scrollbarer Container bei mehr als ORDER_HISTORY_VISIBLE Eintraegen */}
          <div
            className={`rounded-xl overflow-hidden ${isDarkMode ? 'border border-white/10' : 'border border-slate-200'}`}
            style={orderHistory.length > ORDER_HISTORY_VISIBLE ? { maxHeight: '260px', overflowY: 'auto' } : undefined}
          >
            {/* Tabellen-Header */}
            <div className={`grid text-xs font-medium px-3 py-1.5 ${isDarkMode ? 'bg-white/5 text-white/40' : 'bg-slate-50 text-slate-400'}`}
              style={{ gridTemplateColumns: '6rem 1fr 5rem 5.5rem 5.5rem' }}>
              <span>{t('productDetail.orderDate')}</span>
              <span>{t('productDetail.orderVendor')}</span>
              <span className="text-right">{t('productDetail.orderQty')}</span>
              <span className="text-right">{t('productDetail.orderPrice')}</span>
              <span className="text-right">{t('productDetail.orderValue')}</span>
            </div>
            {/* Zeilen */}
            {orderHistory.slice(0, ORDER_HISTORY_VISIBLE).map((row, i) => {
              const dateStr = row.orderDate
                ? new Date(row.orderDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : '—';
              return (
                <div
                  key={`${row.orderNo}-${row.orderItem}-${i}`}
                  className={`grid text-xs px-3 py-1.5 ${
                    isDarkMode
                      ? 'border-t border-white/10 hover:bg-white/5'
                      : 'border-t border-slate-100 hover:bg-slate-50'
                  } transition-colors`}
                  style={{ gridTemplateColumns: '6rem 1fr 5rem 5.5rem 5.5rem' }}
                  title={`Bestellung ${row.orderNo}/${row.orderItem}`}
                >
                  <span className={c.label}>{dateStr}</span>
                  <span className={`${c.value} truncate`} title={row.vendorName || row.vendor}>
                    {row.vendorName || row.vendor || '—'}
                  </span>
                  <span className={`${c.value} text-right`}>{formatQtyDE(row.quantity)} {row.unit}</span>
                  <span className={`${c.value} text-right`}>
                    {row.netPrice != null ? `${formatNumberDE(row.netPrice)} / ${row.priceUnit > 1 ? row.priceUnit : row.unit}` : '—'}
                  </span>
                  <span className={`${c.value} text-right font-medium`}>
                    {row.netValue != null ? `${formatNumberDE(row.netValue)} ${row.currency}` : '—'}
                  </span>
                </div>
              );
            })}
            {/* Hinweis auf weitere Eintraege */}
            {orderHistory.length > ORDER_HISTORY_VISIBLE && (
              <div className={`px-3 py-1.5 text-xs text-center ${isDarkMode ? 'border-t border-white/10 text-white/35' : 'border-t border-slate-100 text-slate-400'}`}>
                {t('productDetail.orderHistoryMore').replace('{count}', orderHistory.length - ORDER_HISTORY_VISIBLE)}
              </div>
            )}
          </div>
        </div>
      )}
    </BlockShell>
  );
};

export default BlockProcurement;
